import ButtonT from '@components/tailwind-components/Button/ButtonT';
import TextareaT from '@components/tailwind-components/Input/TextareaT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import { FileDropZone } from '@components/tailwind-components/Upload/FileDropZone';
import { EMAIL_REGEX } from '@conversed/shared/src/central/constants/regex';
import type { TFile } from '@conversed/shared/src/central/types/common.type';
import type { TRole } from '@conversed/shared/src/central/types/role.types';
import {
  pushUnique,
  removeBase64Prefix,
} from '@conversed/shared/src/helpers/data.helpers';
import { toastErrorTranslated } from '@helpers/toast';
import { getBulkInviteValidationSchema } from '@helpers/validationSchemaHelpers';
import { useScrollTo } from '@hooks/general/viewport/useScrollTo';
import { useModal } from '@hooks/modal/useModal';
import useRole from '@hooks/settings/roles/useRole';
import { useValidationForm } from '@hooks/useValidationForm';
import { parse as parseCSV } from 'csv-parse/browser/esm';
import parse from 'html-react-parser';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import XLSX from 'xlsx-js-style';
import BulkInviteMembersList from './BulkInviteMembersList';
import type { TFormattedEmail } from './bulkinvite.type';

type TFormData = {
  emails: string;
};

const BULK_INVITE_EMAILS_ELEMENT_ID = 'bulk-invite-emails';

const base64ToAOA = async (
  type: string,
  base64: string,
): Promise<string[][]> => {
  const parsedData = window.atob(removeBase64Prefix(base64));

  if (type === 'text/csv') {
    const data = await new Promise<string[][]>((resolve, reject) => {
      const data: string[][] = [];

      const parser = parseCSV(parsedData, { columns: false });

      parser.on('data', (row: string[]) => data.push(row));
      parser.on('error', (err) => {
        reject('members-settings.bulk-invite.error-message.unknown-error');
        console.error(err);
      });
      parser.on('end', () => resolve(data));
    });

    return data;
  }

  // Convert binary string to Uint8Array
  const bytes = new Uint8Array(parsedData.length);
  for (let i = 0; i < parsedData.length; i++) {
    bytes[i] = parsedData.charCodeAt(i);
  }

  // Read the workbook
  const workbook = XLSX.read(bytes, { type: 'array' });

  // Get the first sheet name
  const firstSheetName = workbook.SheetNames[0];

  // Get the sheet
  const sheet = workbook.Sheets[firstSheetName];

  // Convert to array of arrays
  const data: string[][] = XLSX.utils.sheet_to_json(sheet, {
    header: 1, // Ensures output is array of arrays
    blankrows: false,
  });

  return data;
};

export type BulkInviteDataModalProps = {
  emails: TFormattedEmail[];
};

export const BulkInviteDataModal: React.FC<BulkInviteDataModalProps> = ({
  emails: defaultEmails,
}) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const [uploadError, setUploadError] = useState('');

  const { assignableClientRoles } = useRole();
  const [selectedRoles, setSelectedRoles] = useState([
    assignableClientRoles[0],
  ]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<TFile[]>([]);

  const onSubmit = useCallback(
    (formData: TFormattedEmail[]) => {
      setUploadError('');

      if (!formData.length) {
        return toastErrorTranslated(
          'members-settings.bulk-invite.error-message.no-emails',
        );
      }

      return showModal('bulk-invite-members-review', '', {
        members: formData,
      });
    },
    [showModal],
  );

  const { handleSubmit, watch, setValue, control } =
    useValidationForm<TFormData>({
      schema: getBulkInviteValidationSchema(),
      defaultValues: {
        emails: '',
      },
      shouldUseNativeValidation: true,
    });

  const emailText = watch('emails');

  const [emails, setEmails] = useState<TFormattedEmail[]>(defaultEmails || []);

  const { scrollToElement } = useScrollTo();

  useEffect(() => {
    if (emails.length > 0) {
      scrollToElement(BULK_INVITE_EMAILS_ELEMENT_ID);
    }
  }, [emails, scrollToElement]);

  // * Check if all provided roles are valid
  const validateRoles = useCallback(
    (roles: string[]) => {
      const allRolesValid = roles.every((role) =>
        assignableClientRoles.find(
          (assignableRole) => assignableRole.id === role,
        ),
      );

      if (!allRolesValid) {
        // * All roles need to be valid
        throw new Error(
          'members-settings.bulk-invite.error-message.invalid-roles',
        );
      }

      return roles;
    },
    [assignableClientRoles],
  );

  const checkErrors = useCallback(
    (isValidEmail: boolean, isDuplicate: boolean) => {
      if (isValidEmail && !isDuplicate) {
        return;
      }

      if (!isValidEmail) {
        return 'members-settings.bulk-invite.error-message.invalid-email';
      }

      return 'members-settings.bulk-invite.error-message.email-duplicate';
    },
    [],
  );

  const onFilesSelect = useCallback(
    async (files: TFile[]) => {
      // Take only the first file
      const file = files[0];
      if (!file) {
        return;
      }

      // * Zero out the input field
      setValue('emails', '');
      setUploadError('');

      // * Add emails from csv
      const { type, base64 } = file;

      try {
        const data = await base64ToAOA(type, base64);

        // * Remove header as it is unneeded
        data.shift();

        const seen = new Set();
        const formattedEmails = data
          .filter((entry) => entry.length < 3 || entry[2]?.trim())
          .map((entry) => {
            if (entry.length < 3) {
              throw new Error(
                'members-settings.bulk-invite.error-message.invalid-file',
              );
            }

            const [firstName, lastName, email, rolesText] = entry;
            const emailTrimmed = email.trim();
            const rolesSplit = rolesText ? rolesText.split(',') : [];
            const roles =
              rolesSplit.length > 0
                ? validateRoles(rolesSplit)
                : selectedRoles.map(({ id }) => id);
            const isValidEmail = EMAIL_REGEX.test(emailTrimmed);

            // * Find duplicates and mark them
            const isDuplicate = seen.has(emailTrimmed);
            seen.add(emailTrimmed);

            return {
              firstName,
              lastName,
              email: emailTrimmed,
              roles,
              error: checkErrors(isValidEmail, isDuplicate),
            };
          });

        if (formattedEmails.length === 0) {
          return toastErrorTranslated(
            'members-settings.bulk-invite.error-message.no-emails-to-extract',
          );
        }

        setEmails(formattedEmails);
      } catch (error: any) {
        if (
          error.message ===
          'members-settings.bulk-invite.error-message.invalid-roles'
        ) {
          return setUploadError(
            'members-settings.bulk-invite.error-message.invalid-roles',
          );
        }
        return toastErrorTranslated(error.message);
      } finally {
        setSelectedFiles([]);
      }
    },
    [selectedRoles, setValue, validateRoles, checkErrors],
  );

  const extractEmails = useCallback(() => {
    setUploadError('');

    if (!selectedFiles?.length) {
      const seen = new Set();
      const formattedEmails = emailText
        .split('\n')
        .filter((email) => email.trim())
        .map((email) => {
          const emailTrimmed = email.trim();
          const isValidEmail = EMAIL_REGEX.test(emailTrimmed);

          // * Find duplicates and mark them
          const isDuplicate = seen.has(emailTrimmed);
          seen.add(emailTrimmed);

          return {
            email: emailTrimmed,
            roles: selectedRoles.map((selectedRole) => selectedRole.id),
            error: checkErrors(isValidEmail, isDuplicate),
          };
        });

      if (formattedEmails.length === 0) {
        return toastErrorTranslated(
          'members-settings.bulk-invite.error-message.no-emails-to-extract',
        );
      }

      setEmails(formattedEmails);
      return;
    }
  }, [selectedFiles, selectedRoles, checkErrors, emailText]);

  const handleEditRole = useCallback(
    (rowId: number, role: TRole, check: boolean) => {
      return setEmails((prev) =>
        prev.map((row, rIdx) => {
          if (rIdx !== rowId) {
            return row;
          }

          const roles = row.roles;

          if (check) {
            return {
              ...row,
              roles: pushUnique(role.id, roles),
            };
          }

          return {
            ...row,
            roles: roles.filter((rrole) => role.id !== rrole),
          };
        }),
      );
    },
    [],
  );

  const onRoleToggle = useCallback(
    (role: TRole, check?: boolean) => {
      setUploadError('');

      const valueToCompare =
        typeof check === 'undefined'
          ? !selectedRoles.some((selectedRole) => selectedRole.id === role.id)
          : check;
      const newValue = valueToCompare
        ? [...selectedRoles, role]
        : selectedRoles.filter((selectedRole) => selectedRole.id !== role.id);

      setSelectedRoles(newValue);
    },
    [selectedRoles],
  );

  const submitEmails = useCallback(() => {
    const emailValidRoles = emails.every((email) => email.roles.length > 0);

    if (!emailValidRoles) {
      return toastErrorTranslated(
        'members-settings.bulk-invite.error-message.user-with-no-roles',
      );
    }

    return onSubmit(emails);
  }, [onSubmit, emails]);

  return (
    <form className='tailwind-modal-form' onSubmit={handleSubmit(submitEmails)}>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2 relative'>
          <div className='col-span-1'>
            <LabelT
              text={t('members-settings.bulk-invite.emails-description')}
              className='mb-1 text-xs'
            />
            <TextareaT
              field='emails'
              placeholder={t('members-settings.bulk-invite.emails')}
              id='emails'
              control={control}
              options={{
                required: t('settings.template-response.validation.content'),
              }}
              className='text-xs min-h-[110px] resize-none'
            />
          </div>

          <LabelT
            text={t('members-settings.bulk-invite.emails-assign')}
            className='text-xs'
          />

          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 items-center justify-start font-light text-sm mb-3 select-none appearance-none relative'>
            {assignableClientRoles.map((role) => (
              <div key={role.id} className='flex items-center'>
                <input
                  className='cursor-pointer'
                  type='checkbox'
                  id={`role-${role.id}`}
                  value={role.id}
                  checked={selectedRoles.some(
                    (selectedRole) => selectedRole.id === role.id,
                  )}
                  onChange={(e) => onRoleToggle(role, e.target.checked)}
                />
                <LabelT
                  className='pl-2 cursor-pointer text-xs'
                  text={role.title}
                  onClick={() => onRoleToggle(role)}
                />
              </div>
            ))}
          </div>
          <ButtonT
            type='button'
            styling='cancel'
            size='xs'
            rounding='md'
            onClick={extractEmails}>
            {t('members-settings.bulk-invite.emails-extract')}
          </ButtonT>
          <div className='text-center font-medium'>or</div>
          <FileDropZone
            fileInputRef={fileInputRef}
            accept={[
              'text/csv',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ]}
            selectedFiles={selectedFiles}
            onFilesSelect={onFilesSelect}
            onFileRemove={() => {}}
            multiple={false}
          />
          {uploadError && (
            <p className='text-xs font-normal text-red-500'>
              {parse(t(uploadError))}
            </p>
          )}

          <a
            className='text-xs font-normal mt-3 text-gray-600 hover:text-primary-base cursor-pointer w-fit'
            href={
              'https://cdn.conversed.biz/templates/Bulk-Invite-Template.xlsx'
            }>
            ⬇️{' '}
            {t('data-sources.template.download', {
              name: 'Bulk Invite',
            })}
          </a>
        </div>
        <BulkInviteMembersList
          id={BULK_INVITE_EMAILS_ELEMENT_ID}
          members={emails}
          onEditRole={handleEditRole}
        />
      </div>
      <div className='tailwind-modal-footer mt-8'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.next')}
        </ButtonT>
      </div>
    </form>
  );
};

export default BulkInviteDataModal;
